import React, { useRef, useState } from "react";
import { ClickAwayListener, IconButton, Paper, Popper, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface UserDataFieldsInfoProps {
  /**
   * La chiave del titolo. **Assente** per l'informativa degli userdata del
   * processo, che ha il suo testo scritto qui sotto; chi la usa per altro — i
   * campi del configuratore, i campi di un registro — passa la sua chiave e la
   * legge dal dizionario.
   */
  labelKey?: string;
  /** La chiave del testo. Stessa regola di `labelKey`. */
  bodyKey?: string;
  /**
   * Optional className / sx passthrough for the trigger button,
   * useful to align size/position in different layouts (inline in
   * AccordionSummary, next to a section title, etc.).
   */
  triggerSx?: React.ComponentProps<typeof IconButton>["sx"];
  /**
   * Stops click propagation. Set to `true` when the trigger is
   * rendered inside an interactive parent (e.g. AccordionSummary)
   * to avoid toggling the parent on info-click.
   */
  stopPropagation?: boolean;
}

const UserDataFieldsInfo: React.FC<UserDataFieldsInfoProps> = ({
  labelKey,
  bodyKey,
  triggerSx,
  stopPropagation = false,
}) => {
  const translations = useTranslations();
  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  // `getTranslation` restituisce **la chiave** quando la voce manca, quindi un
  // `|| "testo"` non scatterebbe mai e a schermo si leggerebbe
  // `custom_fields.config.expression.help`. Qui la chiave non tradotta si
  // riconosce e prende il posto del ripiego: chi usa il componente per una
  // informativa nuova non deve aspettare il dizionario per farla leggere.
  const risolvi = (chiave: string, ripiego: string) => {
    const risolta = getTranslation(translations, chiave);
    return !risolta || risolta === chiave ? ripiego : risolta;
  };

  // L'informativa degli userdata del processo ha il testo in **italiano scritto
  // qui**, non nel dizionario: la voce vecchia spiegava un pulsante che ora non
  // c'è più («questi dati saranno inseriti solo per questo processo…»), e
  // lasciarla passare dal dizionario significava aspettare che qualcuno
  // aggiornasse sei lingue per leggere una spiegazione sbagliata.
  // Chi passa `labelKey`/`bodyKey` — il configuratore, i registri — ha chiavi
  // che esistono già e continua a leggerle dal dizionario.
  const titolo = "Informazioni sui campi del modello";
  const testo =
    "I campi del modello si definiscono una volta nella master dialog in fase di modellazione e valgono per tutte le istanze del processo.";

  const titleText = labelKey ? risolvi(labelKey, titolo) : titolo;
  const bodyText = bodyKey ? risolvi(bodyKey, testo) : testo;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={handleClick}
        aria-label={titleText}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          p: 0.25,
          ml: 1,
          color: "text.secondary",
          "&:hover": { color: "primary.main", bgcolor: "transparent" },
          ...triggerSx,
        }}
      >
        <InfoOutlined sx={{ fontSize: 18 }} />
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={6}
            sx={{
              p: 1.5,
              maxWidth: 320,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: "3px solid",
              borderLeftColor: "primary.main",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {titleText}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {bodyText}
            </Typography>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default UserDataFieldsInfo;
